import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import * as Analytics from 'analytics/analytics'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import ChatMapWidget from 'tripPlanner/components/Chat/ChatMapWidget'
import ChatMapFilters from 'tripPlanner/components/Chat/ChatMapFilters'
import ChatMapOffersDrawer from '../components/ChatMapOffersDrawer'
import type { MarkerSelectionData } from 'tripPlanner/components/Chat/chatMapConstants'
import type { TripStop } from 'tripPlanner/api/types'
import type { RightPanelView } from '../hooks/useRightPanelState'
import { useSearchResults } from '../hooks/useSearchResults'
import { useMapFilterState } from '../hooks/useMapFilterState'
import { useAICameraState } from '../hooks/useAICameraState'
import { useMapData } from '../hooks/useMapData'
import { useEventSubscription, useTripPlannerEventBus } from '../contexts/TripPlannerEventBus'
import { useChatActions } from '../contexts/ChatActionsContext'
import { useChatStreamStatus } from '../contexts/ChatStreamStatusContext'
import { useChatUiAction } from '../contexts/ChatUiActionContext'
import { useChatStreamMessages } from '../contexts/ChatStreamMessagesContext'
import { useItineraryContext } from '../contexts/ItineraryContext'
import { useMapInteraction } from '../contexts/MapInteractionContext'
import {
  FullHeightMapWrapper,
  MapArea,
  MapOverlayStack,
  MapUpdatingIndicatorPill,
  SearchThisAreaButton,
  SHOW_ON_MAP_ZOOM,
} from '../styles/layoutStyles'

interface Props {
  /** When false, hooks still run (preserving state) but JSX returns null */
  visible: boolean
  selectedDayItineraryId: number | undefined
  selectedStop: TripStop | undefined
  selectedMarkerData: MarkerSelectionData | null
  onMarkerSelect: (data: MarkerSelectionData | null) => void
  setRightPanelView: (view: RightPanelView) => void
  rightPanelView: RightPanelView
}

function MapContainer({
  visible,
  selectedDayItineraryId,
  selectedStop,
  selectedMarkerData,
  onMarkerSelect,
  setRightPanelView,
  rightPanelView,
}: Props) {
  const intl = useIntl()
  const eventBus = useTripPlannerEventBus()

  // ── Context reads ──

  const { sendMessage } = useChatActions()
  const { chatId, chatKey, isAwaitingMessageEnd, loadingState } = useChatStreamStatus()
  const { pendingUiAction, clearPendingUiAction } = useChatUiAction()
  const { messages } = useChatStreamMessages()
  const { itineraryData, enrichingStopIds, enrichingItemIds } = useItineraryContext()
  const { onMarkerHover, onCardHover, highlightedMarkerId } = useMapInteraction()

  // ── Hotel and experience search results ──

  const {
    searchQuery: hotelSearchQuery,
    hotelMapMarkers,
    isHotelFilterVisible,
    setHotelSearchResults,
    setHotelSearchError,
    setHotelSearchResultsFromCarousel,
    hideHotelFilter,
    resetHotelSearch,
    experienceSearchQuery,
    experienceMapMarkers,
    isExperienceFilterVisible,
    setExperienceSearchResultsFromCarousel,
    hideExperienceFilter,
    resetExperienceSearch,
  } = useSearchResults()

  // ── Map filter state ──

  const {
    handleMapViewStateChange,
    handleMapBoundsChange,
    handleHotelFilterDismiss,
    handleExperienceFilterDismiss,
    handleExploreCategorySelect,
    applyHotelFilterState,
    applyExperienceFilterState,
    handleExploreMapFromDrawer,
    handleSearchThisAreaClick,
    shouldShowSearchThisArea,
    isSearchThisAreaDisabled,
    deemphasizeBaseMarkers,
    disableClustering,
    selectedExploreCategory,
    exploreMarkers,
    exploreOffers,
    isExploreFilterVisible,
    isExploreFilterActive,
    isExploreLoading,
    isExploreOffersError,
    refetchExploreOffers,
    hasExploreResults,
    isBoundsDirty,
    resetFilterState,
  } = useMapFilterState({
    setRightPanelView,
    isHotelFilterVisible,
    hideHotelFilter,
    setHotelSearchResultsFromCarousel,
    isExperienceFilterVisible,
    hideExperienceFilter,
    setExperienceSearchResultsFromCarousel,
  })

  const isLeOffersExploreSource = selectedExploreCategory?.source === 'le-hotels' || selectedExploreCategory?.source === 'le-experiences'
  const isChatMapOffersDrawerOpen = isExploreFilterVisible && isLeOffersExploreSource && (hasExploreResults || isExploreLoading || isExploreOffersError)

  // ── AI camera state ──

  const {
    aiCamera,
    aiCameraKey,
    moveMapTo,
    applyUiAction,
    handleAiCameraClear,
    resetCameraState,
  } = useAICameraState({ setRightPanelView })

  // ── Callbacks ──

  const handleExploreMapClick = useCallback((coordinates: { lat: number; lng: number }) => {
    moveMapTo(coordinates.lat, coordinates.lng, SHOW_ON_MAP_ZOOM)
    handleExploreMapFromDrawer()
  }, [moveMapTo, handleExploreMapFromDrawer])

  const handleHotelMapViewClick = useCallback((data: Parameters<typeof applyHotelFilterState>[0]) => {
    applyHotelFilterState(data)
    const offerWithCoords = data.offers.find(o => o.coordinates)
    if (offerWithCoords?.coordinates) {
      moveMapTo(offerWithCoords.coordinates.lat, offerWithCoords.coordinates.lng, SHOW_ON_MAP_ZOOM)
    }
  }, [applyHotelFilterState, moveMapTo])

  const handleShowExperienceOnMap = useCallback((data: Parameters<typeof applyExperienceFilterState>[0]) => {
    applyExperienceFilterState(data)
    const offerWithCoords = data.offers.find(o => o.coordinates)
    if (offerWithCoords?.coordinates) {
      moveMapTo(offerWithCoords.coordinates.lat, offerWithCoords.coordinates.lng, SHOW_ON_MAP_ZOOM)
    }
  }, [applyExperienceFilterState, moveMapTo])

  const handleDeselectExploreCategory = useCallback(() => {
    handleExploreCategorySelect(null)
  }, [handleExploreCategorySelect])

  const handleOfferClick = useCallback((offerId: string, offerType?: string) => {
    eventBus.dispatch({ type: 'open_offer_drawer', offerId, offerType })
  }, [eventBus])

  const handleExploreOfferClick = useCallback((offerId: string, offerType?: string) => {
    handleOfferClick(offerId, offerType)
  }, [handleOfferClick])

  // ── Deselect explore on view change to itinerary ──

  const prevRightPanelViewRef = useRef(rightPanelView)
  useEffect(() => {
    const prev = prevRightPanelViewRef.current
    prevRightPanelViewRef.current = rightPanelView
    if (prev !== 'itinerary' && rightPanelView === 'itinerary') {
      handleDeselectExploreCategory()
    }
  }, [rightPanelView, handleDeselectExploreCategory])

  // ── Event bus subscriptions ──

  useEventSubscription('hotel_search_results', (event) => {
    setHotelSearchResults(event.data, event.generation)
    if (event.data.offers.length === 0) {
      Analytics.trackClientEvent({
        subject: `${event.data.searchQuery}|chatId:${chatId}`,
        action: 'zero_search_results',
        category: 'chat_trip_planner',
        type: 'operational',
      })
    }
  })

  useEventSubscription('hotel_search_error', (event) => {
    setHotelSearchError(event.data.message, event.generation, event.data.hotelSearchCallId)
  })

  useEventSubscription('ui_action', (event) => {
    applyUiAction(event.data)
    clearPendingUiAction()
  })

  // Apply buffered UI action that arrived before this component mounted
  // (e.g., during the landing→chat transition on first message)
  useEffect(() => {
    if (pendingUiAction) {
      applyUiAction(pendingUiAction)
      clearPendingUiAction()
    }
  }, [pendingUiAction, applyUiAction, clearPendingUiAction])

  useEventSubscription('show_hotel_on_map', (event) => {
    handleHotelMapViewClick(event.data)
  })

  useEventSubscription('show_experience_on_map', (event) => {
    handleShowExperienceOnMap(event.data)
  })

  useEventSubscription('show_explore_on_map', (event) => {
    handleExploreMapClick(event.coordinates)
  })

  useEventSubscription('itinerary_changed', () => {
    hideHotelFilter()
    hideExperienceFilter()
    resetFilterState()
  })

  useEventSubscription('reset_chat_state', () => {
    resetHotelSearch()
    resetExperienceSearch()
    resetFilterState()
    resetCameraState()
  })

  // ── Map data + marker merging ──

  const { mapData: baseMapData, hasMapMarkers: hasBaseMapMarkers } = useMapData({
    messages,
    itineraryData,
    selectedDayItineraryId,
    selectedStop,
  })

  const isMapUpdating =
    isExploreLoading ||
    enrichingStopIds.size > 0 || enrichingItemIds.size > 0 ||
    loadingState.isContentStreaming || isAwaitingMessageEnd

  const mergedMarkers = useMemo(() => {
    const hasFilterResults =
      (isHotelFilterVisible && hotelMapMarkers.length > 0) ||
      (isExperienceFilterVisible && experienceMapMarkers.length > 0) ||
      (isExploreFilterActive && exploreMarkers.length > 0)
    // Only hide suggested markers when filter results are ready to replace them.
    // During the loading gap, suggested markers stay visible to preserve the
    // viewport and prevent useMapGlobeRotation from jumping to the default view.
    const base = hasFilterResults
      ? (baseMapData.markers ?? []).filter(m => m.source !== 'suggested')
      : (baseMapData.markers ?? [])
    // Priority: hotel > experience > explore > base
    if (isHotelFilterVisible && hotelMapMarkers.length > 0) return [...hotelMapMarkers, ...base]
    if (isExperienceFilterVisible && experienceMapMarkers.length > 0) return [...experienceMapMarkers, ...base]
    if (isExploreFilterActive && exploreMarkers.length > 0) return [...exploreMarkers, ...base]
    return base
  }, [baseMapData.markers, hotelMapMarkers, experienceMapMarkers, exploreMarkers, isHotelFilterVisible, isExperienceFilterVisible, isExploreFilterActive])

  const mapData = useMemo(() => ({
    ...baseMapData,
    markers: mergedMarkers,
  }), [baseMapData, mergedMarkers])

  const hasMapMarkers = hasBaseMapMarkers ||
    (isHotelFilterVisible && hotelMapMarkers.length > 0) ||
    (isExperienceFilterVisible && experienceMapMarkers.length > 0) ||
    (isExploreFilterActive && exploreMarkers.length > 0)

  const shouldResetToGlobe = !hasMapMarkers && !aiCamera

  // ── i18n ──

  const searchThisAreaLabel = intl.formatMessage({
    id: 'kVSN4s5ZBcxyXhOZlUJO',
    defaultMessage: 'Search this area',
    description: 'Button label to search for offers in the currently visible map area',
  })

  // Convention exception: "Components should not determine whether they render"
  // This container must stay mounted so hooks (useSearchResults, useMapFilterState,
  // useAICameraState) preserve state across Map↔Itinerary view toggles. The parent
  // controls visibility via the `visible` prop; the child only gates JSX output.
  if (!visible) return null

  return (
    <FullHeightMapWrapper>
      <MapArea>
        <MapOverlayStack>
          <ChatMapFilters
            hotelSearchQuery={hotelSearchQuery}
            isHotelFilterVisible={isHotelFilterVisible}
            onHotelFilterDismiss={handleHotelFilterDismiss}
            experienceSearchQuery={experienceSearchQuery}
            isExperienceFilterVisible={isExperienceFilterVisible}
            onExperienceFilterDismiss={handleExperienceFilterDismiss}
            isExploreFiltersVisible={isExploreFilterVisible}
            isExploreFilterActive={isExploreFilterActive}
            selectedExploreCategory={selectedExploreCategory}
            onExploreCategorySelect={handleExploreCategorySelect}
            isExploreLoading={isExploreLoading}
            hasExploreResults={hasExploreResults}
            isBoundsDirty={isBoundsDirty}
          />
          {shouldShowSearchThisArea && (
            <SearchThisAreaButton
              kind="secondary"
              variant="dark"
              size="small"
              shape="round"
              startIcon={<LineSearchIcon />}
              onClick={handleSearchThisAreaClick}
              disabled={isSearchThisAreaDisabled}
            >
              {searchThisAreaLabel}
            </SearchThisAreaButton>
          )}
          {isMapUpdating && (
            <MapUpdatingIndicatorPill>
              <LoadingAnimation size={8} />
            </MapUpdatingIndicatorPill>
          )}
        </MapOverlayStack>
        <ChatMapWidget
          key="trip-planner-map"
          chatKey={chatKey}
          data={mapData}
          fillContainer
          onMarkerSelect={onMarkerSelect}
          highlightedMarkerId={highlightedMarkerId ?? undefined}
          onViewStateChange={handleMapViewStateChange}
          onBoundsChange={handleMapBoundsChange}
          isDrawerOpen={Boolean(selectedMarkerData)}
          shouldResetToGlobe={shouldResetToGlobe}
          deemphasizeBaseMarkers={deemphasizeBaseMarkers}
          exploreOffers={exploreOffers}
          disableClustering={disableClustering}
          onMarkerHover={onMarkerHover}
          onOfferClick={handleOfferClick}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
          aiCamera={aiCamera}
          aiCameraKey={aiCameraKey}
          onAiCameraClear={handleAiCameraClear}
        />
      </MapArea>
      <ChatMapOffersDrawer
        isOpen={isChatMapOffersDrawerOpen}
        onClose={handleDeselectExploreCategory}
        offers={exploreOffers}
        isLoading={isExploreLoading}
        isError={isExploreOffersError}
        onRetry={refetchExploreOffers}
        itineraryData={itineraryData}
        onSendMessage={sendMessage}
        onViewOffer={handleExploreOfferClick}
        highlightedMarkerId={highlightedMarkerId ?? undefined}
        onCardHover={onCardHover}
      />
    </FullHeightMapWrapper>
  )
}

export default MapContainer
